import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import ticketService, { 
  TICKET_STATUS_LABELS, 
  TICKET_STATUS_COLORS,
  TICKET_CATEGORY_LABELS
} from '../services/ticketService';
import { formatBeijingDateTime } from '../utils/dateTimeBeijing';

const TicketDetailModal = ({ ticketId, isOpen, onClose }) => {
  const [ticket, setTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const repliesListRef = useRef(null);
  const ticketRef = useRef(null);
  const repliesRef = useRef([]);

  useEffect(() => {
    if (isOpen && ticketId) {
      loadTicketDetail();
    }
  }, [isOpen, ticketId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 轮询检查工单状态更新（每15秒检查一次，仅在弹框打开且页面可见时）
  useEffect(() => {
    if (!isOpen || !ticketId) return;

    // 页面可见性检测
    const handleVisibilityChange = () => {
      if (document.hidden) {
        return; // 页面不可见时停止轮询
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const pollInterval = setInterval(() => {
      // 只在弹框打开且页面可见时进行轮询
      if (document.hidden) return;

      // 使用轻量级状态检查API
      ticketService.checkTicketStatus(ticketId)
        .then(statusData => {
          const currentTicket = ticketRef.current;
          const currentReplies = repliesRef.current;
          
          // 检查状态或回复数量是否发生变化
          if (currentTicket) {
            const statusChanged = statusData.status !== currentTicket.status;
            const replyCountChanged = statusData.reply_count > currentReplies.length;
            const resolutionChanged = statusData.resolution !== currentTicket.resolution;
            
            if (statusChanged || replyCountChanged || resolutionChanged) {
              // 有变化时，重新加载完整数据
              ticketService.getTicket(ticketId)
                .then(ticketData => {
                  setTicket(ticketData);
                  setReplies(ticketData.replies || []);
                  ticketRef.current = ticketData;
                  repliesRef.current = ticketData.replies || [];
                })
                .catch(err => {
                  console.debug('Failed to reload ticket after status change:', err);
                });
            }
          }
        })
        .catch(err => {
          // 静默失败，不显示错误
          console.debug('Polling ticket status failed:', err);
        });
    }, 15000); // 每15秒检查一次（减少服务器压力）

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, ticketId]);

  const loadTicketDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const ticketData = await ticketService.getTicket(ticketId);
      setTicket(ticketData);
      setReplies(ticketData.replies || []);
      // 更新 ref
      ticketRef.current = ticketData;
      repliesRef.current = ticketData.replies || [];
      
      // 滚动到最新消息
      setTimeout(() => {
        if (repliesListRef.current) {
          repliesListRef.current.scrollTop = repliesListRef.current.scrollHeight;
        }
      }, 100);
      
    } catch (err) {
      console.error('Error loading ticket detail:', err);
      setError(err.message || '加载工单详情失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    
    if (!replyContent.trim()) {
      return;
    }
    
    setIsSubmittingReply(true);
    
    try {
      const replyData = await ticketService.addTicketReply(ticketId, {
        content: replyContent.trim(),
        is_internal: false
      });
      
      setReplies(prev => [...prev, replyData]);
      setReplyContent('');
      
      // 重新加载工单详情以获取最新状态
      await loadTicketDetail();
      
      // 滚动到最新消息
      setTimeout(() => {
        if (repliesListRef.current) {
          repliesListRef.current.scrollTop = repliesListRef.current.scrollHeight;
        }
      }, 100);
      
    } catch (err) {
      console.error('Error submitting reply:', err);
      alert(err.message || '提交回复失败，请稍后重试');
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return formatBeijingDateTime(dateString);
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return String(dateString);
    }
  };

  // 格式化用户名：将"客服管理员"改为"客服"
  const formatUserName = (nickname) => {
    if (nickname === '客服管理员') {
      return '客服';
    }
    return nickname;
  };

  // 当回复列表更新时，自动滚动到底部
  useEffect(() => {
    if (replies.length > 0 && repliesListRef.current) {
      setTimeout(() => {
        repliesListRef.current.scrollTop = repliesListRef.current.scrollHeight;
      }, 100);
    }
  }, [replies.length]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>工单详情</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {loading ? (
            <LoadingContainer>
              <LoadingText>加载中...</LoadingText>
            </LoadingContainer>
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : ticket ? (
            <>
              {/* 工单基本信息 */}
              <TicketInfoCard>
                <TicketHeader>
                  <TicketTitleRow>
                    <TicketTitle>{ticket.title}</TicketTitle>
                    <StatusBadge status={ticket.status}>
                      {TICKET_STATUS_LABELS[ticket.status]}
                    </StatusBadge>
                  </TicketTitleRow>
                </TicketHeader>
                
                <TicketMeta>
                  <MetaItem>
                    <MetaLabel>工单编号：</MetaLabel>
                    #{ticket.ticket_id}
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>分类：</MetaLabel>
                    {TICKET_CATEGORY_LABELS[ticket.category]}
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>创建时间：</MetaLabel>
                    {formatDate(ticket.created_at)}
                  </MetaItem>
                  <MetaItem>
                    <MetaLabel>更新时间：</MetaLabel>
                    {formatDate(ticket.updated_at)}
                  </MetaItem>
                  {ticket.assignee_nickname && (
                    <MetaItem>
                      <MetaLabel>处理人：</MetaLabel>
                      {ticket.assignee_nickname}
                    </MetaItem>
                  )}
                </TicketMeta>
                
                <TicketDescription>
                  <DescriptionLabel>问题描述：</DescriptionLabel>
                  <DescriptionContent>{ticket.description}</DescriptionContent>
                </TicketDescription>
              </TicketInfoCard>

              {/* 回复列表 */}
              <RepliesSection>
                <RepliesTitle>回复记录 ({replies.length})</RepliesTitle>
                
                <RepliesCard>
                  {replies.length === 0 ? (
                    <EmptyReplies>
                      <EmptyRepliesText>暂无回复</EmptyRepliesText>
                    </EmptyReplies>
                  ) : (
                    <RepliesListContainer ref={repliesListRef}>
                      <RepliesList>
                        {replies.map(reply => {
                          // 用户侧：客服在左边，用户在右边
                          // 如果回复的user_id不等于工单的user_id，则是客服，应该左对齐（isUser = true）
                          // 如果回复的user_id等于工单的user_id，则是用户，应该右对齐（isUser = false）
                          const isUser = reply.user_id !== ticket.user_id;
                          return (
                            <ReplyItem key={reply.id} isUser={isUser}>
                              <ReplyBubble isUser={isUser}>
                                <ReplyHeader>
                                  <ReplyAuthor isUser={isUser}>{formatUserName(reply.user_nickname)}</ReplyAuthor>
                                  <ReplyTime isUser={isUser}>{formatDate(reply.created_at)}</ReplyTime>
                                </ReplyHeader>
                                <ReplyContent isUser={isUser}>{reply.content}</ReplyContent>
                              </ReplyBubble>
                            </ReplyItem>
                          );
                        })}
                      </RepliesList>
                    </RepliesListContainer>
                  )}
                </RepliesCard>
              </RepliesSection>

              {/* 回复表单 */}
              <ReplyForm onSubmit={handleReplySubmit}>
                <ReplyFormTitle>添加回复</ReplyFormTitle>
                <ReplyTextArea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="请输入您的回复内容..."
                  rows={3}
                />
                <ReplyFormActions>
                  <SubmitReplyButton type="submit" disabled={isSubmittingReply || !replyContent.trim()}>
                    {isSubmittingReply ? '提交中...' : '提交回复'}
                  </SubmitReplyButton>
                </ReplyFormActions>
              </ReplyForm>
            </>
          ) : (
            <ErrorMessage>工单不存在</ErrorMessage>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default TicketDetailModal;

// 样式组件
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  animation: fadeIn 0.3s ease-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 0;
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  line-height: 1;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 12px 16px 16px;
  overflow-y: auto;
  flex: 1;
`;

const TicketInfoCard = styled.div`
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
`;

const TicketHeader = styled.div`
  margin-bottom: 12px;
`;

const TicketTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const TicketTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  line-height: 1.4;
  flex: 1;
`;

const StatusBadge = styled.span`
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background: ${props => TICKET_STATUS_COLORS[props.status] || '#718096'};
  white-space: nowrap;
`;

const TicketMeta = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  
  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const MetaItem = styled.div`
  font-size: 12px;
  color: #718096;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MetaLabel = styled.span`
  color: #a0aec0;
  font-weight: 500;
`;

const TicketDescription = styled.div`
  margin-bottom: 0;
`;

const DescriptionLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
`;

const DescriptionContent = styled.div`
  font-size: 13px;
  color: #4a5568;
  line-height: 1.5;
  background: white;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
`;



const RepliesSection = styled.div`
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
`;

const RepliesTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 10px 0;
`;

const RepliesCard = styled.div`
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  min-height: 100px;
`;

const RepliesListContainer = styled.div`
  /* 超过3个记录时显示滚动条，每个记录大约120px高度，加上间距约16px，3个记录约360px */
  max-height: 360px;
  overflow-y: auto;
  overflow-x: hidden;
  
  /* 自定义滚动条样式 */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 3px;
    
    &:hover {
      background: #a0aec0;
    }
  }
  
  /* Firefox 滚动条样式 */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #f1f1f1;
`;

const EmptyReplies = styled.div`
  text-align: center;
  padding: 12px;
`;

const EmptyRepliesText = styled.div`
  color: #718096;
  font-size: 13px;
`;

const RepliesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 0;
`;

const ReplyItem = styled.div`
  display: flex;
  justify-content: ${props => props.isUser ? 'flex-start' : 'flex-end'};
  align-items: flex-start;
  width: 100%;
`;

const ReplyBubble = styled.div`
  max-width: 70%;
  padding: 12px 16px;
  border-radius: ${props => props.isUser ? '12px 12px 12px 4px' : '12px 12px 4px 12px'};
  background: ${props => props.isUser ? '#e3f2fd' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: ${props => props.isUser ? '#1a202c' : 'white'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &::before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    ${props => props.isUser 
      ? `
        left: -8px;
        bottom: 12px;
        border-right: 8px solid #e3f2fd;
        border-top: 8px solid transparent;
        border-bottom: 8px solid transparent;
      `
      : `
        right: -8px;
        bottom: 12px;
        border-left: 8px solid #764ba2;
        border-top: 8px solid transparent;
        border-bottom: 8px solid transparent;
      `
    }
  }
  
  @media (max-width: 768px) {
    max-width: 85%;
  }
`;

const ReplyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  gap: 12px;
`;

const ReplyAuthor = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.isUser ? '#1976d2' : 'rgba(255, 255, 255, 0.95)'};
`;

const ReplyTime = styled.div`
  font-size: 11px;
  color: ${props => props.isUser ? '#64748b' : 'rgba(255, 255, 255, 0.8)'};
  white-space: nowrap;
`;

const ReplyContent = styled.div`
  font-size: 13px;
  color: ${props => props.isUser ? '#1a202c' : 'white'};
  line-height: 1.6;
  word-wrap: break-word;
  white-space: pre-wrap;
`;



const ReplyForm = styled.form`
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
`;

const ReplyFormTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 8px 0;
`;

const ReplyTextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;
  background: white;
  resize: vertical;
  min-height: 70px;
  font-family: inherit;
  transition: all 0.2s ease;
  margin-bottom: 10px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const ReplyFormActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SubmitReplyButton = styled.button`
  padding: 6px 14px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 30px 16px;
`;

const LoadingText = styled.div`
  color: #718096;
  font-size: 13px;
`;

const ErrorMessage = styled.div`
  background: #fff5f5;
  color: #c53030;
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid #feb2b2;
  font-size: 13px;
  margin-bottom: 12px;
`;
